/**
 * Robust Vote Persistence System
 * Multiple fallback layers to ensure votes are never lost
 */

const STORAGE_KEY = 'pending_votes_queue'
const MAX_RETRIES = 5
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000] // Exponential backoff in ms

/**
 * Save vote to localStorage as backup
 */
export function saveVoteToLocalStorage(voteData) {
  try {
    const existing = getPendingVotesFromStorage()
    const voteId = voteData.id || `${voteData.company_id}_${voteData.user_id}_${voteData.voteID}`
    
    // Check if vote already exists
    const existingIndex = existing.findIndex(v => v.id === voteId)
    if (existingIndex >= 0) {
      existing[existingIndex] = {
        ...voteData,
        timestamp: Date.now(),
        retryCount: existing[existingIndex].retryCount || 0,
      }
    } else {
      existing.push({
        ...voteData,
        timestamp: Date.now(),
        retryCount: 0,
      })
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing))
    console.log('✅ Vote saved to localStorage backup:', voteId)
    return true
  } catch (error) {
    console.error('❌ Failed to save vote to localStorage:', error)
    return false
  }
}

/**
 * Get all pending votes from localStorage
 */
export function getPendingVotesFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Error reading pending votes from storage:', error)
    return []
  }
}

/**
 * Remove vote from localStorage after successful submission
 */
export function removeVoteFromStorage(voteId) {
  try {
    const existing = getPendingVotesFromStorage()
    const filtered = existing.filter(v => v.id !== voteId)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
    console.log('✅ Vote removed from localStorage:', voteId)
    return true
  } catch (error) {
    console.error('Error removing vote from storage:', error)
    return false
  }
}

/**
 * Send vote to server-side fallback API for persistent logging
 */
async function sendToServerFallback(voteData) {
  try {
    const response = await fetch('/api/vote-fallback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(voteData),
    })
    
    if (!response.ok) {
      throw new Error(`Server fallback failed: ${response.status}`)
    }
    
    const result = await response.json()
    console.log('✅ Vote logged to server fallback:', result)
    return { success: true, result }
  } catch (error) {
    console.error('❌ Server fallback failed:', error)
    return { success: false, error }
  }
}

/**
 * Submit vote with retry logic and multi-layer fallback
 */
export async function submitVoteWithRetry(supabase, voteData, retryCount = 0) {
  const voteId = voteData.id || `${voteData.company_id}_${voteData.user_id}_${voteData.voteID}`
  
  try {
    // Always save to localStorage first (before attempting submission)
    saveVoteToLocalStorage(voteData)
    
    // Attempt submission
    const { error, data } = await supabase.from('votes').upsert([voteData])
    
    if (error) {
      throw error
    }
    
    // Success! Remove from localStorage
    removeVoteFromStorage(voteId)
    console.log('✅ Vote submitted successfully:', voteId)
    
    return { success: true, data, error: null }
  } catch (error) {
    console.error(`❌ Vote submission failed (attempt ${retryCount + 1}):`, error)
    
    // Update retry count in localStorage
    const pending = getPendingVotesFromStorage()
    const voteIndex = pending.findIndex(v => v.id === voteId)
    if (voteIndex >= 0) {
      pending[voteIndex].retryCount = retryCount + 1
      pending[voteIndex].lastError = error.message
      pending[voteIndex].lastAttempt = Date.now()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pending))
    }
    
    // Retry if we haven't exceeded max retries
    if (retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAYS[retryCount] || RETRY_DELAYS[RETRY_DELAYS.length - 1]
      console.log(`⏳ Retrying vote submission in ${delay}ms...`)
      
      await new Promise(resolve => setTimeout(resolve, delay))
      return submitVoteWithRetry(supabase, voteData, retryCount + 1)
    }
    
    // Max retries exceeded - use server-side fallback
    console.warn(`⚠️ Max retries exceeded. Attempting server-side fallback...`)
    const fallbackResult = await sendToServerFallback(voteData)
    
    if (fallbackResult.success) {
      console.log('✅ Vote successfully logged to server (will be manually recovered)')
      // Keep in localStorage as additional backup
      return { success: false, data: null, error, serverLogged: true }
    }
    
    // Server fallback also failed - vote is only in localStorage
    console.error('⚠️ All fallback methods failed. Vote only saved in localStorage.')
    return { success: false, data: null, error, serverLogged: false }
  }
}

/**
 * Retry all pending votes from localStorage
 * Call this periodically or on page load
 */
export async function retryPendingVotes(supabase) {
  const pending = getPendingVotesFromStorage()
  
  if (pending.length === 0) {
    return { retried: 0, succeeded: 0, failed: 0 }
  }
  
  console.log(`🔄 Retrying ${pending.length} pending vote(s)...`)
  
  let succeeded = 0
  let failed = 0
  
  for (const vote of pending) {
    // Skip votes that have exceeded max retries
    if (vote.retryCount >= MAX_RETRIES) {
      console.warn(`⏭️ Skipping vote (max retries exceeded):`, vote.id)
      failed++
      continue
    }
    
    // Remove metadata before submitting
    const { timestamp, retryCount, lastError, lastAttempt, ...voteData } = vote
    
    try {
      const result = await submitVoteWithRetry(supabase, voteData, retryCount)
      if (result.success) {
        succeeded++
      } else {
        failed++
      }
    } catch (error) {
      console.error('Error retrying vote:', error)
      failed++
    }
  }
  
  console.log(`✅ Retry complete: ${succeeded} succeeded, ${failed} failed`)
  return { retried: pending.length, succeeded, failed }
}

/**
 * Get statistics about pending votes
 */
export function getPendingVotesStats() {
  const pending = getPendingVotesFromStorage()
  return {
    total: pending.length,
    byRetryCount: pending.reduce((acc, vote) => {
      const count = vote.retryCount || 0
      acc[count] = (acc[count] || 0) + 1
      return acc
    }, {}),
    oldest: pending.length > 0 ? Math.min(...pending.map(v => v.timestamp)) : null,
  }
}

/**
 * Export all pending votes as JSON (for manual recovery)
 */
export function exportPendingVotes() {
  const pending = getPendingVotesFromStorage()
  const dataStr = JSON.stringify(pending, null, 2)
  const dataBlob = new Blob([dataStr], { type: 'application/json' })
  const url = URL.createObjectURL(dataBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = `pending_votes_${Date.now()}.json`
  link.click()
  URL.revokeObjectURL(url)
  return pending
}

